import * as React from 'react';
import {
  Box,
  Typography,
  MenuList,
  MenuItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Switch,
  TextField,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Autocomplete,
  Tooltip
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { CONST, TokenValidator } from '../../utils';
import { Settings } from '../../controllers';
import TemplatesEditor from './templatesEditor';
import PtsOffset from './ptsOffset';

const compact = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, '');

const DEFAULT_PRODUCTS = ['aligner', 'retainer', 'refinement', 'template'];

const emptyProductRow = () => ({ product: '', code: '', zpl: '' });

// Groups raw fgs/zpls/label_print rows into logical clients (name variants merged)
const groupClients = (overview) => {
    const map = {};

    const getClient = (name) => {
        const key = compact(name);
        if(!map[key]){
            map[key] = { key: key, names: [], products: {}, labelPrint: [] };
        }
        if(!map[key].names.includes(name)){
            map[key].names.push(name);
        }
        return map[key];
    };

    const getProduct = (client, product) => {
        const pkey = compact(product);
        if(!client.products[pkey]){
            client.products[pkey] = { product: product, fgsRows: [], zplRows: [] };
        }
        return client.products[pkey];
    };

    (overview.fgs || []).forEach(x => {
        getProduct(getClient(x.client), x.product).fgsRows.push(x);
    });
    (overview.zpls || []).forEach(x => {
        getProduct(getClient(x.client), x.product).zplRows.push(x);
    });
    (overview.label_print || []).forEach(x => {
        getClient(x.client).labelPrint.push(x);
    });

    return Object.values(map)
        .map(c => ({
            ...c,
            display: c.names.find(n => n.includes(' ')) || c.names[0],
            productList: Object.values(c.products).sort((a, b) => a.product.localeCompare(b.product))
        }))
        .sort((a, b) => a.display.localeCompare(b.display));
};

export default function Main() {

    document.title = 'Platform Settings';

    const [loading, setLoading] = React.useState(false);
    const [overview, setOverview] = React.useState({ fgs: [], zpls: [], label_print: [], templates: [] });
    const [view, setView] = React.useState('clients');
    const [expanded, setExpanded] = React.useState({});
    const [edits, setEdits] = React.useState({});
    const [busy, setBusy] = React.useState(false);

    const [newClientUi, showNewClientUi] = React.useState(false);
    const [newClient, setNewClient] = React.useState({ name: '', labelPrint: true, products: [emptyProductRow()] });

    const [addProductsUi, showAddProductsUi] = React.useState(null);
    const [addProducts, setAddProducts] = React.useState([emptyProductRow()]);


    const clients = React.useMemo(() => groupClients(overview), [overview]);
    const productOptions = React.useMemo(() => {
        const found = new Set(DEFAULT_PRODUCTS);
        (overview.fgs || []).forEach(x => found.add(x.product));
        return Array.from(found).sort();
    }, [overview]);

    React.useEffect(() => {
        load();
    }, []);

    const token = async() => await TokenValidator.GetInternalUser();

    const load = async() => {
        setLoading(true);

        const response = await Settings.GetOverview(await token());

        setLoading(false);

        if(response === undefined || !response.success){
            alert(response === undefined ? 'Error loading settings' : response.message);
            return;
        }

        setOverview(response.result);
        setEdits({});
    }

    // ---------- Clients & FGS ----------

    const editKey = (clientKey, productKey) => `${clientKey}|${productKey}`;

    const getEdit = (clientKey, group) => edits[editKey(clientKey, compact(group.product))] || {};

    const setEdit = (clientKey, group, patch) => {
        const key = editKey(clientKey, compact(group.product));
        setEdits({ ...edits, [key]: { ...(edits[key] || {}), ...patch } });
    };

    const currentCode = (clientKey, group) => {
        const e = getEdit(clientKey, group);
        return e.code !== undefined ? e.code : (group.fgsRows[0] ? group.fgsRows[0].code : '');
    };

    const currentZpl = (clientKey, group) => {
        const e = getEdit(clientKey, group);
        return e.zpl !== undefined ? e.zpl : (group.zplRows[0] ? group.zplRows[0].zpl : '');
    };

    const currentActive = (clientKey, group) => {
        const e = getEdit(clientKey, group);
        return e.active !== undefined ? e.active : (group.fgsRows[0] ? group.fgsRows[0].active : true);
    };

    const hasEdit = (clientKey, group) => Object.keys(getEdit(clientKey, group)).length > 0;

    const saveProduct = async(client, group) => {
        const e = getEdit(client.key, group);
        const tkn = await token();

        setBusy(true);

        if(e.code !== undefined || e.active !== undefined){
            if(e.code !== undefined && !String(e.code).trim()){
                setBusy(false);
                alert('FGS code cannot be empty');
                return;
            }

            const updates = group.fgsRows.map(x => ({
                id: x.id,
                ...(e.code !== undefined ? { code: e.code } : {}),
                ...(e.active !== undefined ? { active: e.active } : {})
            }));

            const response = await Settings.UpdateFgs(tkn, updates);

            if(response === undefined || !response.success){
                setBusy(false);
                alert(response === undefined ? 'Error updating FGS' : response.message);
                return;
            }
        }

        if(e.zpl !== undefined && e.zpl !== ''){
            let updates;

            if(group.zplRows.length > 0){
                updates = group.zplRows.map(x => ({ id: x.id, zpl: e.zpl }));
            }else{
                updates = client.names.map(n => ({ client: n, product: group.product, zpl: e.zpl }));
            }

            const response = await Settings.UpdateZpl(tkn, updates);

            if(response === undefined || !response.success){
                setBusy(false);
                alert(response === undefined ? 'Error updating ZPL' : response.message);
                return;
            }
        }

        setBusy(false);
        await load();
    }

    const labelPrintActive = (client) => client.labelPrint.length > 0 && client.labelPrint.some(x => x.active);

    const toggleLabelPrint = async(client) => {
        setBusy(true);

        const response = await Settings.SetLabelPrint(await token(), client.display, !labelPrintActive(client));

        setBusy(false);

        if(response === undefined || !response.success){
            alert(response === undefined ? 'Error updating label print' : response.message);
            return;
        }

        await load();
    }

    // ---------- New Client dialog ----------

    const validProducts = (rows) => {
        const cleaned = rows.filter(x => String(x.product).trim() && String(x.code).trim());
        return cleaned.length > 0 && cleaned.length === rows.filter(x => String(x.product).trim() || String(x.code).trim()).length ? cleaned : null;
    };

    const handleCreateClient = async() => {
        if(!newClient.name.trim()){
            alert('Client name is required');
            return;
        }

        const products = validProducts(newClient.products);

        if(products === null){
            alert('Every product row needs both a product name and an FGS code');
            return;
        }

        setBusy(true);

        const response = await Settings.CreateClient(await token(), newClient.name, newClient.labelPrint, products);

        setBusy(false);

        if(response === undefined || !response.success){
            alert(response === undefined ? 'Error creating client' : response.message);
            return;
        }

        showNewClientUi(false);
        setNewClient({ name: '', labelPrint: true, products: [emptyProductRow()] });
        await load();
        alert(response.message);
    }

    const handleAddProducts = async() => {
        const products = validProducts(addProducts);

        if(products === null){
            alert('Every product row needs both a product name and an FGS code');
            return;
        }

        setBusy(true);

        const response = await Settings.AddProducts(await token(), addProductsUi.display, products);

        setBusy(false);

        if(response === undefined || !response.success){
            alert(response === undefined ? 'Error adding products' : response.message);
            return;
        }

        showAddProductsUi(null);
        setAddProducts([emptyProductRow()]);
        await load();
        alert(response.message);
    }

    // ---------- Render helpers ----------

    const renderProductRows = (rows, setRows) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rows.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    <Autocomplete
                        freeSolo
                        disableClearable
                        options={productOptions}
                        inputValue={row.product}
                        onInputChange={(e, v) => {
                            const tmp = [...rows];
                            tmp[i] = { ...tmp[i], product: v };
                            setRows(tmp);
                        }}
                        sx={{ width: 190 }}
                        renderInput={(params) => <TextField {...params} label='Product' size='small' />}
                    />
                    <TextField
                        label='FGS Code'
                        size='small'
                        sx={{ width: 150 }}
                        value={row.code}
                        onChange={(e) => {
                            const tmp = [...rows];
                            tmp[i] = { ...tmp[i], code: e.target.value };
                            setRows(tmp);
                        }}
                    />
                    <Select
                        native
                        size='small'
                        sx={{ width: 220 }}
                        value={row.zpl}
                        onChange={(e) => {
                            const tmp = [...rows];
                            tmp[i] = { ...tmp[i], zpl: e.target.value };
                            setRows(tmp);
                        }}
                    >
                        <option value=''>-- no label (ZPL) --</option>
                        {(overview.templates || []).map(t => (
                            <option key={t.name} value={t.name}>{t.name}</option>
                        ))}
                    </Select>
                    <IconButton
                        size='small'
                        disabled={rows.length < 2}
                        onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    >
                        <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                </Box>
            ))}
            <Button
                startIcon={<AddIcon />}
                size='small'
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => setRows([...rows, emptyProductRow()])}
            >
                Add Product Row
            </Button>
        </Box>
    );

    const ClientsView = () => (
        <Box sx={{ padding: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                <Typography variant='h5' sx={{ flexGrow: 1 }}>Clients &amp; FGS Codes</Typography>
                <Button variant='contained' startIcon={<AddIcon />} onClick={() => showNewClientUi(true)} disabled={busy}>
                    New Client
                </Button>
                <IconButton onClick={load} disabled={loading || busy}><RefreshIcon /></IconButton>
            </Box>

            <Typography variant='body2' sx={{ marginBottom: 2, color: 'gray' }}>
                Each client is matched against the order field "case_client". Clients whose name contains spaces are stored
                with both name variants (with and without spaces) so every lookup finds them. "Print FGS Label" controls
                whether the FGS label prints at bagging for that client.
            </Typography>

            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: CONST.Colors.Primary.Main }}>
                            <TableCell sx={{ color: 'white', width: 40 }} />
                            <TableCell sx={{ color: 'white' }}>Client</TableCell>
                            <TableCell sx={{ color: 'white' }}>Name Variants</TableCell>
                            <TableCell sx={{ color: 'white' }} align='center'>Products</TableCell>
                            <TableCell sx={{ color: 'white' }} align='center'>Print FGS Label</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clients.map(client => (
                            <React.Fragment key={client.key}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded({ ...expanded, [client.key]: !expanded[client.key] })}>
                                    <TableCell>
                                        <IconButton size='small'>
                                            {expanded[client.key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{client.display}</TableCell>
                                    <TableCell>
                                        {client.names.map(n => <Chip key={n} label={n} size='small' sx={{ marginRight: 0.5 }} />)}
                                    </TableCell>
                                    <TableCell align='center'>{client.productList.length}</TableCell>
                                    <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                                        <Switch
                                            checked={labelPrintActive(client)}
                                            disabled={busy}
                                            onChange={() => toggleLabelPrint(client)}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ padding: 0, borderBottom: expanded[client.key] ? undefined : 'none' }}>
                                        <Collapse in={!!expanded[client.key]} timeout='auto' unmountOnExit>
                                            <Box sx={{ padding: 2, backgroundColor: '#fafafa' }}>
                                                <Table size='small'>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Product</TableCell>
                                                            <TableCell>FGS Code</TableCell>
                                                            <TableCell>Label Template (ZPL)</TableCell>
                                                            <TableCell align='center'>Active</TableCell>
                                                            <TableCell align='center'>Save</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {client.productList.map(group => (
                                                            <TableRow key={group.product}>
                                                                <TableCell sx={{ width: 160 }}>{group.product}</TableCell>
                                                                <TableCell sx={{ width: 180 }}>
                                                                    <TextField
                                                                        size='small'
                                                                        value={currentCode(client.key, group)}
                                                                        onChange={(e) => setEdit(client.key, group, { code: e.target.value })}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ width: 260 }}>
                                                                    <Select
                                                                        native
                                                                        size='small'
                                                                        fullWidth
                                                                        value={currentZpl(client.key, group)}
                                                                        onChange={(e) => setEdit(client.key, group, { zpl: e.target.value })}
                                                                    >
                                                                        <option value=''>-- none --</option>
                                                                        {(overview.templates || []).map(t => (
                                                                            <option key={t.name} value={t.name}>{t.name}</option>
                                                                        ))}
                                                                        {currentZpl(client.key, group) !== '' &&
                                                                            !(overview.templates || []).some(t => t.name === currentZpl(client.key, group)) && (
                                                                            <option value={currentZpl(client.key, group)}>
                                                                                {currentZpl(client.key, group)} (missing file!)
                                                                            </option>
                                                                        )}
                                                                    </Select>
                                                                </TableCell>
                                                                <TableCell align='center'>
                                                                    <Switch
                                                                        checked={currentActive(client.key, group)}
                                                                        onChange={(e) => setEdit(client.key, group, { active: e.target.checked })}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align='center'>
                                                                    <IconButton
                                                                        color='primary'
                                                                        disabled={!hasEdit(client.key, group) || busy}
                                                                        onClick={() => saveProduct(client, group)}
                                                                    >
                                                                        <SaveIcon />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    size='small'
                                                    sx={{ marginTop: 1 }}
                                                    disabled={busy}
                                                    onClick={() => { setAddProducts([emptyProductRow()]); showAddProductsUi(client); }}
                                                >
                                                    Add Product to {client.display}
                                                </Button>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );

    const MenuItems = [
        { name: 'Clients & FGS', view: 'clients' },
        { name: 'Label Designer', view: 'templates' },
        { name: 'PTS Offset', view: 'ptsOffset' },
    ];

    return(
        <Box
            sx={{
                display: 'flex',
                width: "100%",
                height: "100%",
            }}
        >
            <Box
                sx={{
                    width: 130,
                    flexShrink: 0,
                    backgroundColor: CONST.Colors.Primary.Main
                }}
            >
                <Divider sx={{ borderColor: CONST.Colors.Font.White }} />
                <MenuList>
                    {MenuItems.map((item) => (
                        <MenuItem key={item.view} onClick={() => setView(item.view)} selected={view === item.view}>
                            <ListItemText sx={{ color: CONST.Colors.Font.White }}>
                                <Typography variant='body2'>{item.name}</Typography>
                            </ListItemText>
                        </MenuItem>
                    ))}
                </MenuList>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {/* ClientsView se llama como función (no como <Componente/>): definirla
                    dentro de Main y montarla como componente la remontaría en cada
                    render y los TextField perderían el foco en cada tecla. */}
                {view === 'clients' ? ClientsView()
                    : view === 'ptsOffset' ? <PtsOffset />
                    : <TemplatesEditor overview={overview} reloadOverview={load} />}
            </Box>

            {/* New Client dialog */}
            <Dialog open={newClientUi} onClose={() => showNewClientUi(false)} maxWidth='md' fullWidth>
                <DialogTitle>New Client</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label='Client Name'
                                size='small'
                                sx={{ width: 300 }}
                                value={newClient.name}
                                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                                helperText='As it comes in orders (case_client), e.g. "smile shapers"'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newClient.labelPrint}
                                        onChange={(e) => setNewClient({ ...newClient, labelPrint: e.target.checked })}
                                    />
                                }
                                label='Print FGS Label at bagging'
                            />
                        </Box>

                        <Typography variant='subtitle2'>Products &amp; FGS Codes</Typography>
                        {renderProductRows(newClient.products, (rows) => setNewClient({ ...newClient, products: rows }))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => showNewClientUi(false)}>Cancel</Button>
                    <Button variant='contained' onClick={handleCreateClient} disabled={busy}>
                        {busy ? <CircularProgress size={22} /> : 'Create Client'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Products dialog */}
            <Dialog open={addProductsUi !== null} onClose={() => showAddProductsUi(null)} maxWidth='md' fullWidth>
                <DialogTitle>Add Products to {addProductsUi ? addProductsUi.display : ''}</DialogTitle>
                <DialogContent>
                    <Box sx={{ paddingTop: 1 }}>
                        {renderProductRows(addProducts, setAddProducts)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => showAddProductsUi(null)}>Cancel</Button>
                    <Button variant='contained' onClick={handleAddProducts} disabled={busy}>
                        {busy ? <CircularProgress size={22} /> : 'Add Products'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
